/* Plan 57 T7, owner 2026-09-21: choosing a run at home must not mean choosing
   it again on the product page. IndexedDB carries the File locally across the
   navigation; the home page neither reads its bytes nor sends them anywhere.
   One fixed slot bounds storage, and taking it deletes it in the SAME transaction
   before any preview can use it. The PDP still owns sanitization and uploading. */
(function () {
  'use strict';

  var DATABASE = 'traceform-handoff';
  var STORE = 'uploads';
  var KEY = 'pending';
  var MAX_AGE = 10 * 60 * 1000;

  /* Plan 72 T3: the second slot. Instagram's in-app browser reloads a page that
     sat in the background while the customer was in Strava, and the reload comes
     back with an empty field. The last run that rendered, and the three choices
     it rendered with, stay on this device for a day so that reload can render it
     again. One fixed key bounds the storage to one run. */
  var ROUNDTRIP = 'tf-roundtrip';
  var LAST = 'last';
  var ROUNDTRIP_MAX_AGE = 24 * 60 * 60 * 1000;
  var ROUNDTRIP_MAX_BYTES = 15 * 1024 * 1024;

  /* One readwrite transaction on `database`. `work(store, settle)` queues its
     requests; the promise resolves with what it settled once the transaction
     commits, and with null on any failure, after `ms`, or once the function
     handed to `expose` is called. */
  function withStore(database, ms, work, expose) {
    return new Promise(function (resolve) {
      var db;
      var transaction;
      var settled = false;
      var result = null;
      /* Private browsing, quota failures and an upgrade blocked by another tab
         must not strand the buyer on home. Abort a late write rather than leaving
         a file that an unrelated visit could unexpectedly pick up. */
      var timer = setTimeout(function () { finish(null); }, ms);
      function finish(value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (transaction && value === null) {
          try { transaction.abort(); } catch (err) { /* already complete */ }
        }
        if (db) db.close();
        resolve(value);
      }
      if (expose) expose(function () { finish(null); });
      try {
        var request = window.indexedDB.open(database, 1);
        request.onupgradeneeded = function () {
          request.result.createObjectStore(STORE);
        };
        request.onerror = request.onblocked = function () { finish(null); };
        request.onsuccess = function () {
          db = request.result;
          if (settled) { db.close(); return; }
          try {
            transaction = db.transaction(STORE, 'readwrite');
            var store = transaction.objectStore(STORE);
            transaction.onabort = transaction.onerror = function () { finish(null); };
            transaction.oncomplete = function () { finish(result); };
            work(store, function (value) { result = value; });
          } catch (err) { finish(null); }
        };
      } catch (err) { finish(null); }
    });
  }

  /* Both bounds: a timestamp in the future is a clock moved back, and reading it
     as fresh would keep a record alive past its day. */
  function fresh(entry, maxAge) {
    if (!entry || !(entry.file instanceof File) || typeof entry.timestamp !== 'number') return false;
    var age = Date.now() - entry.timestamp;
    return age >= 0 && age < maxAge;
  }

  function transfer(file) {
    return withStore(DATABASE, 2000, function (store, settle) {
      if (file) {
        store.put({ file: file, timestamp: Date.now() }, KEY);
        settle(true);
        return;
      }
      var read = store.get(KEY);
      read.onsuccess = function () {
        var entry = read.result;
        // Delete even expired or malformed entries. Only a committed
        // deletion releases the File, so two PDP tabs cannot consume it.
        store.delete(KEY);
        if (fresh(entry, MAX_AGE)) settle(entry.file);
      };
    });
  }

  /* Cancels the last save if it is still running. A delete queued behind a slow
     save would give up at two seconds, the page would leave for checkout, and the
     run kept before that save would outlive the purchase. */
  var stopSaving = function () {};

  function forget() {
    stopSaving();
    return withStore(ROUNDTRIP, 2000, function (store) { store.delete(LAST); });
  }

  window.TraceformHandoff = {
    take: function () { return transfer(null); },
    /* `choices` is `{ style, variant, audience }`; `since` is when this file
       first rendered, so a re-render or a restore never extends its day. A file
       over the cap is not kept, and the run before it is dropped: it is no
       longer the current one. Fifteen seconds, not two: nothing waits on this
       write, and a phone can take a while to copy a large photo. */
    remember: function (file, choices, since) {
      if (file.size > ROUNDTRIP_MAX_BYTES) return forget();
      /* One save at a time: a second one would replace the handle, and Buy
         could then cancel only the newer save while the older one still lands. */
      stopSaving();
      return withStore(ROUNDTRIP, 15000, function (store, settle) {
        store.put({ file: file, choices: choices, timestamp: since }, LAST);
        settle(true);
      }, function (stop) { stopSaving = stop; });
    },
    /* Not consumed on read: the in-app browser can reload the page again. An
       expired or malformed record is deleted in the same transaction. */
    recall: function () {
      return withStore(ROUNDTRIP, 2000, function (store, settle) {
        var read = store.get(LAST);
        read.onsuccess = function () {
          var entry = read.result;
          if (fresh(entry, ROUNDTRIP_MAX_AGE) && entry.choices && typeof entry.choices === 'object') settle(entry);
          else if (entry !== undefined) store.delete(LAST);
        };
      });
    },
    forget: forget,
  };

  Array.prototype.forEach.call(document.querySelectorAll('[data-testid="hero-upload"]'), function (input) {
    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      if (!file || input.disabled) return;
      input.disabled = true;
      var target = input.getAttribute('data-product-url');
      transfer(file).then(function (saved) {
        if (saved) {
          var url = new URL(target, window.location.href);
          url.searchParams.set('from', 'home-upload');
          window.location.assign(url.href);
        } else {
          // Storage is an enhancement, never a new error for the buyer to solve.
          window.location.assign(target);
        }
      });
    });
    // A cached home page must still offer its picker after returning from the PDP.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) { input.disabled = false; input.value = ''; }
    });
  });
})();
